
import React, { useState, useCallback } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

interface ImageOptimizerProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: "lazy" | "eager";
  priority?: boolean;
  fallbackSrc?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
  sizes?: string;
  blurDataURL?: string;
  fetchPriority?: "high" | "low" | "auto";
}

const ImageOptimizer: React.FC<ImageOptimizerProps> = ({
  src,
  alt,
  className = "",
  width,
  height,
  loading = "eager", // Default to eager loading for critical images
  priority = true, // Default to high priority for critical images
  fallbackSrc = "/placeholder.svg",
  objectFit = 'cover',
  objectPosition = 'center',
  sizes,
  blurDataURL,
  fetchPriority = "auto"
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  const handleImageLoad = useCallback(() => {
    console.log('Image loaded successfully:', src);
    setIsLoading(false);
  }, [src]);
  
  const handleImageError = useCallback(() => {
    console.error(`Failed to load image: ${src}`);
    setIsLoading(false);
    setHasError(true);
  }, [src]);
  
  // Calculate dimensions for skeleton and container
  const containerStyle = {
    width: width ? `${width}px` : '100%', 
    height: height ? `${height}px` : 'auto',
    minHeight: isLoading ? '100px' : undefined,
    aspectRatio: width && height ? `${width} / ${height}` : undefined,
  };
  
  // For hero images, never show a skeleton
  const isHeroImage = src.includes('ac7da958-8971-47d6-8588-0611fa790b37');
  const showSkeleton = isLoading && !isHeroImage && (!priority || fetchPriority !== "high");
  
  return (
    <div 
      className={`relative overflow-hidden ${isHeroImage ? 'hero-image-container' : ''}`}
      style={containerStyle}
    >
      {showSkeleton && (
        <Skeleton 
          className={`absolute inset-0 ${className}`} 
          style={containerStyle}
        />
      )}
      
      <img
        src={hasError ? fallbackSrc : src}
        alt={alt}
        className={`${className} transition-opacity duration-200`}
        width={width}
        height={height}
        loading={isHeroImage ? "eager" : loading}
        decoding={isHeroImage || priority ? "sync" : "async"}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{
          objectFit,
          objectPosition,
          width: '100%',
          height: '100%',
          // For hero images, always show immediately
          opacity: isHeroImage || (priority && fetchPriority === "high") ? 1 : (isLoading ? 0 : 1)
        }}
        // Correctly use fetchPriority in camelCase for React JSX
        fetchPriority={isHeroImage ? "high" : fetchPriority}
        {...(sizes ? { sizes } : {})}
      />
    </div>
  );
};

export default React.memo(ImageOptimizer);
